import React, { useState } from "react";
import { X, Megaphone, Check, Trash2 } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";
import { Select } from "./Pickers";

// Admin-only composer. Replaces the current active announcement (the RPC
// deactivates any previous active row first).
export function AnnouncementComposer({ C, dark, onClose, onPosted }) {
  const [message, setMessage] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [tone, setTone] = useState("info");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const post = async () => {
    if (!message.trim()) { setErr("Message required."); return; }
    setBusy(true); setErr(null);
    try {
      const { error } = await supabase.rpc("admin_post_announcement", {
        p_message: message.trim(),
        p_link_url: linkUrl.trim() || null,
        p_link_label: linkLabel.trim() || null,
        p_tone: tone,
      });
      if (error) throw error;
      onPosted && onPosted();
      onClose && onClose();
    } catch (e) {
      setErr(e.message || "Post failed");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true); setErr(null);
    try {
      const { error } = await supabase.rpc("admin_dismiss_announcement", { p_id: null });
      if (error) throw error;
      onPosted && onPosted();
      onClose && onClose();
    } catch (e) {
      setErr(e.message || "Dismiss failed");
    } finally {
      setBusy(false);
    }
  };

  const field = {
    width: "100%", padding: "10px 12px", fontSize: 13,
    background: dark ? "rgba(255,255,255,0.04)" : "#F5F5F5",
    border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
    outline: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
  };
  const label = { display: "block", fontSize: 10, fontWeight: 800, color: C.textDim, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 };

  return (
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:5600,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(3px)"}}/>
      <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:5601,
        width:"92%",maxWidth:520,
        background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:16,padding:"22px 24px",
        fontFamily:"'DM Sans', sans-serif",color:C.text}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{width:32,height:32,borderRadius:8,background:`${GOLD}22`,border:`1px solid ${GOLD}66`,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Megaphone size={16} color={GOLD}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.4,textTransform:"uppercase"}}>Admin</div>
            <div style={{fontSize:17,fontWeight:800}}>Post announcement</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{background:"transparent",border:"none",cursor:"pointer",padding:6,color:C.textMid}}>
            <X size={17}/>
          </button>
        </div>

        <div style={{marginBottom:12}}>
          <label style={label}>Message</label>
          <textarea value={message} onChange={e=>setMessage(e.target.value)}
            placeholder="e.g. New Rikki Broadmore drills drop Friday - check them out."
            rows={3}
            style={{...field,resize:"vertical",minHeight:72}}/>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={label}>Tone</label>
            <Select value={tone} onChange={e=>setTone(e.target.value)} style={field}>
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warn">Warning</option>
            </Select>
          </div>
          <div>
            <label style={label}>Link label (optional)</label>
            <input value={linkLabel} onChange={e=>setLinkLabel(e.target.value)} placeholder="Open" style={field}/>
          </div>
        </div>

        <div style={{marginBottom:12}}>
          <label style={label}>Link URL (optional)</label>
          <input value={linkUrl} onChange={e=>setLinkUrl(e.target.value)} placeholder="https://..." style={field}/>
        </div>

        {err && <div style={{padding:"10px 12px",borderRadius:9,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.35)",color:"#F87171",marginBottom:10,fontSize:12.5,lineHeight:1.5}}>{err}</div>}

        <div style={{display:"flex",gap:8,marginTop:6}}>
          <button onClick={deactivate} disabled={busy}
            style={{padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,cursor:"pointer",
              border:"1px solid rgba(248,113,113,0.35)",background:"rgba(248,113,113,0.08)",color:"#F87171",
              display:"inline-flex",alignItems:"center",gap:6,opacity:busy?.6:1}}>
            <Trash2 size={12}/> Dismiss active
          </button>
          <div style={{flex:1}}/>
          <button onClick={onClose}
            style={{padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,cursor:"pointer",
              border:`1px solid ${C.border}`,background:"transparent",color:C.textMid}}>Cancel</button>
          <button onClick={post} disabled={busy}
            style={{padding:"10px 18px",borderRadius:9,fontSize:12.5,fontWeight:800,cursor:busy?"default":"pointer",
              border:"none",background:GOLD,color:"#111",boxShadow:`0 4px 14px ${GOLD}44`,
              display:"inline-flex",alignItems:"center",gap:6,opacity:busy?.7:1}}>
            <Check size={13}/> {busy ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
    </>
  );
}

export default AnnouncementComposer;
